import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { isAgent } from "../services/auth";
import { toast } from "react-toastify";
import LoadingSpinner from "../components/LoadingSpinner";
import PageHeader from "../components/common/PageHeader";
import Button from "../components/common/Button";
import { Plus, Users, X } from "lucide-react";
import useSubscriptionLimits from "../hooks/useSubscriptionLimits";
import SubscriptionLimitModal from "../components/SubscriptionLimitModal";
import { UsageLimitWarning } from "../components/UsageLimitWarning";

const GroupList = () => {
  const agentRole = isAgent();
  const [groups, setGroups] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: "", description: "" });
  const [isLoading, setIsLoading] = useState(true);
  const [hoveredGroup, setHoveredGroup] = useState(null);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [limitInfo, setLimitInfo] = useState(null);
  const navigate = useNavigate();
  const { subscription, handleApiError, checkLimit, usage, refetch } =
    useSubscriptionLimits();

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    setIsLoading(true);
    try {
      const res = await api.get("/groups");
      setGroups(res.data.groups);
    } catch (err) {
      console.error("Error fetching groups", err);
      toast.error("Failed to load groups");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroup.name.trim()) {
      toast.error("Group name is required");
      return;
    }

    // Check if creating a group would exceed limit
    const groupLimit = checkLimit("groups");
    if (groupLimit.limit !== -1 && groupLimit.current >= groupLimit.limit) {
      setLimitInfo({
        feature: "groups",
        current: groupLimit.current,
        limit: groupLimit.limit,
        planName: subscription?.subscriptionPlan?.name,
      });
      setShowLimitModal(true);
      return;
    }

    try {
      await api.post("/groups/create", newGroup);
      setShowModal(false);
      setNewGroup({ name: "", description: "" });
      toast.success("Group created successfully");
      fetchGroups();
      // Refresh subscription usage so modal counts stay accurate
      try {
        await refetch?.();
      } catch (e) {
        // ignore refetch errors
      }
      // Notify other parts of the app (e.g., SubscriptionStatus) to refresh usage counts
      try {
        window.dispatchEvent(new CustomEvent("subscription-usage-refresh"));
      } catch (e) {
        // no-op if CustomEvent is unavailable
      }
    } catch (err) {
      console.error("Error creating group", err);

      // Handle subscription limit errors
      const limitError = handleApiError(err);
      if (limitError.isLimitError) {
        setLimitInfo({
          feature: limitError.feature,
          current: limitError.current,
          limit: limitError.limit,
          planName: subscription?.subscriptionPlan?.name,
        });
        setShowLimitModal(true);
        setShowModal(false); // Close create modal
      } else {
        // Handle other errors
        const errorMessage =
          err.response?.data?.message || "Failed to create group";
        toast.error(errorMessage);
      }
    }
  };

  const handleUpgrade = () => {
    setShowLimitModal(false);
    navigate("/subscription-plans");
  };

  return (
    <div className="p-4 md:p-8">
      <PageHeader
        title="Customer Groups"
        subtitle="Manage and organize your customer groups"
        action={
          !agentRole && (
            <Button
              variant="primary"
              onClick={async () => {
                try {
                  await refetch?.();
                } catch (e) {
                  // ignore refetch errors on open
                }
                setShowModal(true);
              }}
              icon={<Plus className="w-5 h-5" />}
            >
              Create Group
            </Button>
          )
        }
      />

      {/* Group Cards Grid */}
      {isLoading ? (
        <LoadingSpinner fullScreen={true} size="lg" text="Loading groups..." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {groups.length > 0 ? (
            groups.map((group) => (
              <div
                key={group.id}
                onClick={() => navigate(`/groups/${group.id}/customers`)}
                onMouseEnter={() => setHoveredGroup(group.id)}
                onMouseLeave={() => setHoveredGroup(null)}
                className={`
                  bg-white shadow-sm rounded-lg p-5 cursor-pointer
                  transition-all duration-300 transform hover:-translate-y-1
                  ${
                    hoveredGroup === group.id
                      ? "shadow-md ring-2 ring-blue-100"
                      : ""
                  }
                `}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-xl font-bold mb-2 text-gray-800">
                      {group.name}
                    </h3>
                    <p className="text-gray-600 text-sm line-clamp-2">
                      {group.description}
                    </p>
                  </div>
                  <div className="bg-blue-50 p-2 rounded-full">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
                <div className="mt-4 flex items-center justify-between text-sm">
                  <p className="text-gray-400">
                    Created {new Date(group.createdAt).toLocaleDateString()}
                  </p>
                  <p className="text-gray-400">
                    {group.userCustomerCount ?? 0} contacts
                  </p>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full flex flex-col items-center justify-center p-8 bg-white rounded-lg shadow-sm">
              <Users className="w-12 h-12 text-gray-400 mb-4" />
              <p className="text-gray-500 text-lg mb-2">No groups found</p>
              <p className="text-gray-400 text-sm">
                Create your first group to get started
              </p>
            </div>
          )}
        </div>
      )}

      {/* Create Group Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-sm w-full max-w-md">
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold text-gray-800">
                  Create New Group
                </h3>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-4">
              {/* Usage Warning */}
              {usage && subscription && (
                <UsageLimitWarning
                  feature="groups"
                  current={usage.groups || 0}
                  limit={subscription.subscriptionPlan?.maxGroups || -1}
                  onUpgrade={handleUpgrade}
                />
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Group Name
                </label>
                <input
                  type="text"
                  placeholder="Enter group name"
                  value={newGroup.name}
                  onChange={(e) =>
                    setNewGroup({ ...newGroup, name: e.target.value })
                  }
                  className="w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 
                    focus:ring-blue-500 transition-shadow"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  placeholder="Enter group description"
                  value={newGroup.description}
                  onChange={(e) =>
                    setNewGroup({ ...newGroup, description: e.target.value })
                  }
                  rows="3"
                  className="w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 
                    focus:ring-blue-500 transition-shadow resize-none"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 
                    transition transform active:scale-95"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateGroup}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 
                    transition transform active:scale-95"
                >
                  Create Group
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Limit Modal */}
      {showLimitModal && limitInfo && (
        <SubscriptionLimitModal
          isOpen={showLimitModal}
          onClose={() => setShowLimitModal(false)}
          feature={limitInfo.feature}
          currentUsage={limitInfo.current}
          limit={limitInfo.limit}
          planName={limitInfo.planName}
          onUpgrade={handleUpgrade}
        />
      )}
    </div>
  );
};

export default GroupList;
